import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { IChannel } from '@/entities/channel';
import {
  TelegramPersonalAuthDialog,
  WhatsAppPersonalAuthDialog,
} from '@/features/channels';
import {
  TOldChatsAction,
  useUpdatePhone,
} from '@/features/channels/model/useUpdatePhone';
import { IChannelDetailsDto } from '@/shared/api/channels';
import { Box } from '@/shared/ui';
import { Dialog } from '@/shared/ui/Dialog';
import { Button } from '@/shared/ui/lib/Button';
import { CustomTypography } from '@/shared/ui/Typography';

import { SettingBlockItem } from './SettingBlockItem';
import {
  getPhoneChangeAuthProtocol,
  shouldUseWhatsappPhoneAuth,
} from './phoneChangeAuthProtocol';
import { getChannelSettingsPhone } from './channelSettingsPhone';

type TPhoneSettingsBlock = {
  channel: IChannel;
};

export const ChangePhoneNumberBlock = ({ channel }: TPhoneSettingsBlock) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { updatePhone, isPending } = useUpdatePhone();

  const [newChannel, setNewChannel] = useState<IChannelDetailsDto | null>(null);
  const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false);
  const [isChatPolicyDialogOpen, setIsChatPolicyDialogOpen] = useState(false);
  const phoneNumber = getChannelSettingsPhone(channel);
  const authChannel = newChannel
    ? ({
        ...newChannel,
        protocol: getPhoneChangeAuthProtocol(channel, newChannel) ?? newChannel.protocol,
      } as IChannel)
    : null;

  const handleChangePhone = async (oldChatsAction: TOldChatsAction) => {
    await updatePhone(channel.id, oldChatsAction, (response: IChannelDetailsDto) => {
      setNewChannel(response);
      setIsChatPolicyDialogOpen(false);
      setIsAuthDialogOpen(true);
    });
  };

  const handleAuthClose = () => {
    setIsAuthDialogOpen(false);
    navigate('/channels');
  };

  const handleAuthorize = () => {
    setIsAuthDialogOpen(false);
    if (newChannel) {
      navigate(`/channels/${newChannel.id}/info/settings`);
    } else {
      navigate('/channels');
    }
  };

  return (
    <SettingBlockItem title={t('channels:settings.number')}>
      <CustomTypography color="text.secondary">
        {phoneNumber || '-'}
      </CustomTypography>
      <Button variant="text" onClick={() => setIsChatPolicyDialogOpen(true)}>
        {t('channels:settings.changePhoneNumber')}
      </Button>

      <Dialog
        open={isChatPolicyDialogOpen}
        onClose={() => setIsChatPolicyDialogOpen(false)}
        title={t('channels:settings.changePhone')}
        leftButtonSlot={
          <Button
            variant="outlined"
            onClick={() => setIsChatPolicyDialogOpen(false)}
            disabled={isPending}
          >
            {t('common:buttons.cancel')}
          </Button>
        }
        rightButtonSlot={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => handleChangePhone('keep')}
              loading={isPending}
            >
              {t('channels:settings.changePhoneDialog.keepChats')}
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleChangePhone('delete')}
              loading={isPending}
            >
              {t('channels:settings.changePhoneDialog.deleteChats')}
            </Button>
          </Box>
        }
      >
        <CustomTypography sx={{ mb: 2, mt: 2 }} variant="body1">
          {t('channels:settings.changePhoneDialog.confirmChangingPhone')}
        </CustomTypography>
        <CustomTypography color="text.secondary" variant="body2">
          {t('channels:settings.changePhoneDialog.oldChatsQuestion')}
        </CustomTypography>
      </Dialog>

      {authChannel && (
        shouldUseWhatsappPhoneAuth(channel, newChannel) ? (
          <WhatsAppPersonalAuthDialog
            open={isAuthDialogOpen}
            onClose={handleAuthClose}
            onAuthorize={handleAuthorize}
            channel={authChannel}
          />
        ) : (
          <TelegramPersonalAuthDialog
            open={isAuthDialogOpen}
            onClose={handleAuthClose}
            onAuthorize={handleAuthorize}
            channel={authChannel}
          />
        )
      )}
    </SettingBlockItem>
  );
};
